import React from "react";
import { poToolingReadiness } from "@/lib/po-tooling-readiness";
import {
  Package,
  Printer,
  Wrench,
  FileCheck,
  CheckCircle2,
  Play,
  ArrowRight,
  Repeat,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface POProcessWorkflowRibbonProps {
  productionOrder: any;
  routingSteps?: any[];
  activeTab?: string;
  onNavigateTab: (tab: string) => void;
  onSelectStep: (stepId: string) => void;
  highlightStepId?: string | null;
}

export function POProcessWorkflowRibbon({
  productionOrder,
  routingSteps = [],
  activeTab,
  onNavigateTab,
  onSelectStep,
  highlightStepId,
}: POProcessWorkflowRibbonProps) {
  const po = productionOrder;

  const { plateToolings, moldToolings, isPlateReady, isPlateBurning, isToolingReady, plateCountText, plateColorCountText, moldCountText } = poToolingReadiness(po);
  if (!po) return null;

  // ── 4 TRỤ CỘT SẴN SÀNG LÊN MÁY (PRE-FLIGHT READINESS) ──

  // 1. Vật tư
  const isMaterialReady = po.materialStatus === "READY";

  // 2. Kẽm CTP

  // 3. Khuôn gá

  // 4. File duyệt
  const isFileReady = po.isFileApproved || po.prepressStatus === "READY";

  // Flatten & Sort Routing Steps
  const steps = [...(routingSteps || [])].sort(
    (a: any, b: any) => (a.stepOrder || 0) - (b.stepOrder || 0)
  );

  const completedCount = steps.filter((s: any) => s.status === "COMPLETED").length;

  return (
    <div
      className="p-2.5 bg-slate-50/80 rounded-lg border border-slate-200/80 shadow-2xs mb-4 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-2.5"
      data-testid="po-general-workflow-ribbon"
    >
      {/* ── KHỐI 1: 4 TRỤ CỘT SẴN SÀNG LÊN MÁY (PRE-FLIGHT READINESS) ── */}
      <div className="flex flex-wrap items-center gap-1.5 shrink-0">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-0.5">
          Tiền trạm:
        </span>

        {/* Vật tư */}
        <button
          type="button"
          data-testid="preflight-material-btn"
          onClick={() => onNavigateTab("materials")}
          className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium border transition-colors cursor-pointer",
            isMaterialReady
              ? "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"
              : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
          )}
          title="Nhấn để mở Tab Kế hoạch Vật tư"
        >
          <Package className="h-3 w-3 shrink-0 text-slate-500" />
          <span>{isMaterialReady ? "✓ Vật tư sẵn sàng" : "⏳ Chờ cấp vật tư"}</span>
        </button>

        {/* Kẽm CTP */}
        <button
          type="button"
          data-testid="preflight-plate-btn"
          onClick={() => onNavigateTab("toolings")}
          className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium border transition-colors cursor-pointer",
            isPlateReady
              ? "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"
              : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
          )}
          title="Nhấn để mở Tab Khuôn & Kẽm CTP"
        >
          <Printer className="h-3 w-3 shrink-0 text-slate-500" />
          <span>
            {isPlateReady
              ? `✓ Kẽm CTP (${plateCountText})`
              : isPlateBurning
              ? "🔥 Đang ghi bản kẽm..."
              : `⏳ Chờ ghi bản kẽm (${plateCountText})`}
          </span>
        </button>

        {/* Khuôn gá */}
        <button
          type="button"
          data-testid="preflight-mold-btn"
          onClick={() => onNavigateTab("toolings")}
          className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium border transition-colors cursor-pointer",
            isToolingReady
              ? "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"
              : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
          )}
          title="Nhấn để mở Tab Khuôn & Kẽm CTP"
        >
          <Wrench className="h-3 w-3 shrink-0 text-slate-500" />
          <span>
            {isToolingReady
              ? `✓ Khuôn gá (${moldCountText})`
              : `⏳ Chờ xuất khuôn (${moldCountText})`}
          </span>
        </button>

        {/* File in & TCKT */}
        <div
          data-testid="preflight-file-badge"
          className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium border",
            isFileReady
              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
              : "bg-amber-50 text-amber-800 border-amber-200"
          )}
        >
          <FileCheck className="h-3 w-3 shrink-0 text-slate-500" />
          <span>{isFileReady ? "✓ File & TCKT duyệt" : "⏳ Chờ duyệt file"}</span>
        </div>
      </div>

      {/* ── KHỐI 2: DẢI PIPELINE STEPPER TIẾN ĐỘ LUỒNG CÔNG NGHỆ ── */}
      {steps.length > 0 && (
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none py-0.5 min-w-0 flex-1 lg:justify-end">
          <span className="text-[11px] text-slate-500 font-semibold flex items-center gap-1 shrink-0 mr-1 hidden sm:flex">
            <Repeat className="h-3.5 w-3.5 text-indigo-600" />
            <span className="font-mono">
              ({completedCount}/{steps.length})
            </span>
          </span>

          {steps.map((step: any, idx: number) => {
            const stepName =
              step.name || step.stepName || step.process?.name || `Bước ${idx + 1}`;
            const isCompleted = step.status === "COMPLETED";
            const isRunning =
              step.status === "RUNNING" || step.status === "IN_PROGRESS";
            const isOutsource = Boolean(step.isOutsourced);
            const stepId = step.id || step.stepId;
            const isHighlighted = Boolean(
              highlightStepId &&
                (step.id === highlightStepId || step.stepId === highlightStepId)
            );

            return (
              <React.Fragment key={stepId || idx}>
                <button
                  type="button"
                  data-testid={`workflow-step-${idx + 1}`}
                  onClick={() => {
                    if (stepId) {
                      onSelectStep(stepId);
                    } else {
                      onNavigateTab("operations");
                    }
                  }}
                  title={`Bấm để chuyển tới công đoạn ${idx + 1}: ${stepName}`}
                  className={cn(
                    "group h-7 px-2 rounded-full text-xs font-medium border flex items-center gap-1.5 shrink-0 transition-all select-none cursor-pointer hover:shadow-xs hover:scale-[1.02]",
                    isHighlighted
                      ? "bg-indigo-100 text-indigo-900 border-indigo-400 ring-2 ring-indigo-400 font-bold"
                      : isCompleted
                      ? "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
                      : isRunning
                      ? "bg-amber-50 text-amber-900 border-amber-300 ring-2 ring-amber-200/50 hover:bg-amber-100 animate-pulse font-semibold"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                  )}
                >
                  <span
                    className={cn(
                      "h-4 w-4 rounded-full flex items-center justify-center font-bold text-[9px] font-mono shrink-0",
                      isCompleted
                        ? "bg-emerald-600 text-white"
                        : isRunning
                        ? "bg-amber-500 text-white"
                        : "bg-slate-100 text-slate-600 group-hover:bg-slate-200"
                    )}
                  >
                    {idx + 1}
                  </span>

                  <span className="whitespace-nowrap text-[11px]">
                    {stepName}
                  </span>

                  {isOutsource && (
                    <span className="text-[9px] px-1 py-0 bg-purple-100 text-purple-700 rounded font-semibold shrink-0">
                      GC
                    </span>
                  )}

                  {isCompleted ? (
                    <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" />
                  ) : isRunning ? (
                    <Play className="h-2.5 w-2.5 text-amber-600 fill-amber-600 shrink-0" />
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0" />
                  )}
                </button>

                {idx < steps.length - 1 && (
                  <ArrowRight className="h-3 w-3 text-slate-300 shrink-0" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
